import { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button, Loading, returnStatuses } from '@reveni-io/reveni-ui'

import CustomerDashboardLinkAlert from '../components/CustomerDashboardLinkAlert/CustomerDashboardLinkAlert'
import AppContext from '../context/AppContext'
import useCancellationsPortal from '../hooks/useCancellationsPortal/useCancellationsPortal'
import useTransactionTypes from '../hooks/useTransactionTypes/useTransactionTypes'
import routes from '../Router/routes.constants'
import useReturnGroup from './hooks/useReturnGroup/useReturnGroup'
import ProcessCompletedMessage from './ProcessCompletedMessage/ProcessCompletedMessage'
import ProcessCompletedTitle from './ProcessCompletedTitle/ProcessCompletedTitle'
import ProductsRecommendations from './ProductsRecommendations/ProductsRecommendations'
import ReturnLabels from './ReturnLabels/ReturnLabels'
import { getCustomerEmail, redirect, shouldRenderCheckoutUrlButton } from './helpers'
import * as Styles from './styles'

const ProcessCompletedPage = () => {
  const { context, setContext } = useContext(AppContext)
  const { t } = useTranslation()
  const navigation = useNavigate()
  const refundMethod = context?.orderInfo?.allowed_refund_methods?.find(rm => rm.name === context.orderInfo.refundType)

  const { getOrderTransactionType } = useTransactionTypes()
  const orderTransactionType = getOrderTransactionType()
  const { isCancellationsPortal } = useCancellationsPortal()
  const { orders, loading: fetchingAvailableOrders } = useReturnGroup({
    storeId: context?.merchantInfo?.storeId,
    allowsMultiOrderReturns: context?.merchantInfo?.preferences?.allows_multi_order_returns,
  })

  useEffect(() => {
    setContext(context => ({ ...context, finished: true }))
  }, [setContext])

  useEffect(() => {
    if (!context.orderInfo.returnId || !context.orderInfo.id) {
      navigation(routes.notFoundPage)
    }
  })

  const onContinue = () => {
    redirect({
      url: context.orderInfo.success_url,
      slug: context.merchantInfo.slug,
      orderStatus: context.orderInfo.status,
      identifier: context.orderInfo.identifier,
      orderNumber: context.orderInfo.number,
    })
  }

  const getButtonLabel = () => {
    if (context.orderInfo.status === returnStatuses.cancelled) {
      return t('Solicitar cambio')
    }

    return t('Volver a comprar')
  }

  const goToCheckoutPage = url => {
    open(url)
  }

  return (
    <Loading show={fetchingAvailableOrders}>
      <ProcessCompletedTitle
        orderTransactionType={orderTransactionType}
        paymentPending={context?.orderInfo?.exchanged_for_order?.needs_payment}
        orderStatus={context?.orderInfo?.status}
      />
      <ProcessCompletedMessage
        orderAmount={context.orderAmount}
        refundMethod={refundMethod}
        discountInfo={context?.orderInfo?.discountCode}
        storeName={context?.merchantInfo?.name}
        storeSlug={context?.merchantInfo?.slug}
        customerEmail={getCustomerEmail(context?.orderInfo)}
        exchangedForOrder={context?.orderInfo?.exchanged_for_order}
        orderTransactionType={orderTransactionType}
        orderStatus={context?.orderInfo?.status}
        storeReplyToEmail={context?.merchantInfo?.contact_email}
        issueExchangeAtStatus={context?.orderInfo?.issue_exchange_at_status}
        isCancellationsPortal={isCancellationsPortal}
        returnGroup={context?.orderInfo?.return_group}
        orders={orders}
        contactMethod={context?.merchantInfo?.preferences?.contact_method}
        contactUrl={context?.merchantInfo?.contact_url}
        refundOnHold={context?.orderInfo?.refund_on_hold}
      />
      <Styles.ButtonsContainer>
        {shouldRenderCheckoutUrlButton(context?.orderInfo) && (
          <Button fullWidth onClick={() => goToCheckoutPage(context?.orderInfo?.exchanged_for_order?.checkout_url)}>
            {t('Pagar total pendiente')}
          </Button>
        )}
        <ReturnLabels returnLabels={context?.orderInfo?.returnLabels} />
        <ProductsRecommendations />
        <Button
          variant={shouldRenderCheckoutUrlButton(context?.orderInfo) ? 'outlined' : 'contained'}
          fullWidth
          onClick={onContinue}
        >
          {getButtonLabel()}
        </Button>
        {!isCancellationsPortal && context?.orderInfo?.customers_url && (
          <CustomerDashboardLinkAlert customerDashboardUrl={context?.orderInfo?.customers_url} />
        )}
      </Styles.ButtonsContainer>
    </Loading>
  )
}

export default ProcessCompletedPage
